import { useCallback, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { getProject } from '../api/projects';
import { listTags } from '../api/tags';
import { Breadcrumbs, type Crumb } from '../components/Nav';
import { DataView, NotFound } from '../components/States';
import { TagManagerDialog } from '../components/TagManager';
import { ProjectContext, type ProjectContextValue } from '../lib/project';
import { useResource } from '../lib/useResource';

const SECTIONS = [
  { path: 'presentations', label: 'Presentations', match: ['presentations'] },
  { path: 'library', label: 'Library', match: ['library', 'slides', 'imports'] },
  { path: 'groups', label: 'Groups', match: ['groups'] },
  { path: 'events', label: 'Events', match: ['events'] },
  { path: 'template', label: 'Template', match: ['template'] },
  { path: 'history', label: 'History', match: ['history'] },
] as const;

export function ProjectShell() {
  const { projectId } = useParams();
  const id = Number(projectId);
  const project = useResource(() => getProject(id), [id]);
  const tags = useResource(() => listTags(id), [id]);
  const [detailCrumb, setDetailCrumbState] = useState<string | null>(null);
  const [managingTags, setManagingTags] = useState(false);
  const [tagsVersion, setTagsVersion] = useState(0);
  const location = useLocation();

  const setDetailCrumb = useCallback((label: string | null) => setDetailCrumbState(label), []);

  const segments = location.pathname.split('/').filter(Boolean);
  const sectionSegment = segments[2] ?? 'presentations';
  const isDetail = segments.length > 3;
  const section = SECTIONS.find((s) => (s.match as readonly string[]).includes(sectionSegment));

  const context = useMemo<ProjectContextValue | null>(() => {
    if (!project.data) return null;
    return {
      project: project.data,
      tags: tags.data ?? [],
      tagsVersion,
      reloadTags: tags.refresh,
      reloadProject: project.refresh,
      setDetailCrumb,
    };
  }, [project.data, tags.data, tagsVersion, tags.refresh, project.refresh, setDetailCrumb]);

  if (!Number.isInteger(id) || id <= 0) {
    return <NotFound what="project" />;
  }

  return (
    <DataView resource={project} what="project" loadingLabel="Loading project…">
      {(p) => {
        const crumbs: Crumb[] = [
          { label: 'Projects', to: '/' },
          { label: p.name, to: `/projects/${p.id}` },
        ];
        if (section) crumbs.push({ label: section.label, to: `/projects/${p.id}/${section.path}` });
        if (isDetail && detailCrumb) crumbs.push({ label: detailCrumb });
        return (
          <ProjectContext.Provider value={context}>
            <div className="project-shell-header">
              <Breadcrumbs items={crumbs} />
              <div className="project-shell-title">
                <div className="row">
                  <h1>{p.name}</h1>
                  {p.archived ? <span className="badge">Archived</span> : null}
                </div>
                <button type="button" className="btn btn-sm" onClick={() => setManagingTags(true)}>
                  Tags{tags.data ? ` (${tags.data.length})` : ''}
                </button>
              </div>
              <nav className="tabs" aria-label="Project sections">
                {SECTIONS.map((s) => (
                  <NavLink
                    key={s.path}
                    to={`/projects/${p.id}/${s.path}`}
                    className={() => `tab${section?.path === s.path ? ' active' : ''}`}
                    aria-current={section?.path === s.path ? 'page' : undefined}
                  >
                    {s.label}
                  </NavLink>
                ))}
              </nav>
            </div>
            {context ? <Outlet /> : null}
            {managingTags && context ? (
              <TagManagerDialog
                onClose={() => {
                  setManagingTags(false);
                  setTagsVersion((v) => v + 1);
                }}
              />
            ) : null}
          </ProjectContext.Provider>
        );
      }}
    </DataView>
  );
}
